import React, { useEffect, useState } from "react";
import { 
    Box, Button, CircularProgress, TextField, Typography, 
    Paper, Container, IconButton, Divider, Stack 
} from "@mui/material";
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addStuff } from '../../../redux/userRelated/userHandle';
import { underControl } from '../../../redux/userRelated/userSlice';
import { updateSclass, getClassDetails, deleteSclass } from '../../../redux/sclassRelated/sclassHandle';
import { useToast } from "../../../context/ToastContext";
import Classroom from "../../../assets/classroom.png";
import styled, { keyframes } from "styled-components";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SchoolIcon from '@mui/icons-material/School';
import SaveIcon from '@mui/icons-material/Save';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AddClass = () => {
    const [sclassNames, setSclassNames] = useState([{ sclassName: "" }]);

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { id } = useParams() 

    const userState = useSelector(state => state.user);
    const sclassState = useSelector(state => state.sclass);
    
    const { status, currentUser, response, error, tempDetails } = userState;
    const { sclassDetails } = sclassState;

    const adminID = currentUser._id
    const address = "Sclass"

    const { showToast } = useToast();
    const [loader, setLoader] = useState(false)
    
    const isEditMode = !!id;

    useEffect(() => {
        if (isEditMode && sclassDetails && sclassDetails._id !== id) {
            dispatch(getClassDetails(id, "Sclass"));
        }
    }, [isEditMode, id, dispatch, sclassDetails]);

    useEffect(() => {
        if (isEditMode && sclassDetails && sclassDetails._id === id) {
            setSclassNames([{ sclassName: sclassDetails.sclassName || "" }]);
        }
    }, [isEditMode, id, sclassDetails]);

    const handleSclassNameChange = (index, event) => {
        const values = [...sclassNames];
        values[index].sclassName = event.target.value;
        setSclassNames(values);
    };

    const handleAddField = () => {
        setSclassNames([...sclassNames, { sclassName: "" }]);
    };

    const handleRemoveField = (index) => {
        const values = [...sclassNames];
        values.splice(index, 1);
        setSclassNames(values);
    };

    const submitHandler = async (event) => {
        event.preventDefault()
        setLoader(true)
        
        if (isEditMode) {
            const fields = { sclassName: sclassNames[0].sclassName, adminID };
            dispatch(updateSclass(fields, id, address))
        } else {
            // Sequential submission to avoid Redux state race conditions
            for (let i = 0; i < sclassNames.length; i++) {
                if (sclassNames[i].sclassName.trim() !== "") {
                    const fields = { sclassName: sclassNames[i].sclassName, adminID };
                    // Only the last dispatch will trigger the redirection logic in useEffect via the 'added' status
                    dispatch(addStuff(fields, address));
                }
            }
        }
    };

    useEffect(() => {
        if (status === 'added' && tempDetails && !isEditMode) {
            navigate("/Admin/classes")
            dispatch(underControl())
            setLoader(false)
        }
        else if (status === 'failed') {
            showToast(response, 'error')
            setLoader(false)
        }
        else if (status === 'error') {
            showToast("Network Error", 'error')
            setLoader(false)
        }
        else if (status === 'added' && isEditMode) {
            showToast("Class updated successfully", 'success')
            dispatch(underControl())
            setLoader(false)
        }
    }, [status, navigate, error, response, dispatch, tempDetails, isEditMode]);

    return (
        <StyledPageContainer>
            <StyledContainer maxWidth="sm">
                <StyledPaper elevation={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#f5f5f5' }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                                {isEditMode ? "Edit Class" : "Add Class"} 🏫
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {isEditMode ? "Update class name" : "Create a new classroom section"}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <img
                            src={Classroom}
                            alt="classroom"
                            style={{ width: '60%', maxWidth: '250px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
                        />
                    </Box>

                    <form onSubmit={submitHandler}>
                        <Stack spacing={3}>
                            {sclassNames.map((sclassField, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <TextField
                                        fullWidth
                                        label="Class Name"
                                        variant="outlined"
                                        value={sclassField.sclassName}
                                        onChange={(event) => handleSclassNameChange(index, event)}
                                        required
                                        placeholder="e.g. 10"
                                        InputProps={{
                                            startAdornment: <SchoolIcon sx={{ color: 'action.active', mr: 1 }} />,
                                        }}
                                    />
                                    {!isEditMode && sclassNames.length > 1 && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() => handleRemoveField(index)}
                                            sx={{ borderRadius: '10px', height: '100%', py: 1.8 }}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </Box>
                            ))}
                            
                            {!isEditMode && (
                                <Button
                                    variant="outlined"
                                    onClick={handleAddField}
                                    sx={{ borderRadius: '10px', py: 1.2, fontWeight: 600, borderStyle: 'dashed', borderWidth: '2px' }}
                                >
                                    + Add Another Class
                                </Button>
                            )}
                            
                            <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate(-1)}
                                    sx={{ 
                                        borderRadius: '10px', 
                                        px: 4, 
                                        py: 1.2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        flex: 1
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="contained" 
                                    type="submit" 
                                    disabled={loader}
                                    startIcon={loader ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    sx={{ 
                                        borderRadius: '10px', 
                                        px: 6, 
                                        py: 1.2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        flex: 2,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                                        '&:hover': {
                                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                                        }
                                    }}
                                >
                                    {loader ? 'Processing...' : (isEditMode ? "Update Class" : "Create Class")}
                                </Button>
                            </Box>
                        </Stack>
                    </form>
                </StyledPaper>
            </StyledContainer>
        </StyledPageContainer>
    );
}

export default AddClass;

const StyledPageContainer = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  padding-top: 80px;
  padding-bottom: 40px;
`;

const StyledContainer = styled(Container)`
  animation: ${fadeIn} 0.6s ease-out;
`;

const StyledPaper = styled(Paper)`
  padding: 40px;
  border-radius: 24px !important;
  background-color: white !important;
  box-shadow: 0 10px 40px rgba(0,0,0,0.06) !important;
  border: 1px solid #eee;
`;
